import { ParsedUrlQuery } from 'querystring';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { trackBeginCheckout } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import { analyticsBookingType } from 'Util/_HumanWritten/Analytics/analyticsBookingFields';

import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { getValidDestinationLocations } from 'Global/JourneyElements/Utils/getValidDestinationLocations';
import useAddOns from 'Hooks/useAddOns';
import { FerryTripEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import {
	getBookingWizardParameters,
	getCachedBookingWizardData,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardHelpers';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { PaymentCheckOutProvider } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import { BookingWizardData } from './BookingWizardData/BaseBookingWizardData';
import { getBookingWizardDataCacheToken, removeBookingWizardDataCacheToken } from './BookingWizardDataCacheToken';
import { getBookingWizardData, useBookingWizardDataStore } from './BookingWizardDataStore';
import { BookingWizardWrapInner } from './BookingWizardWrapInner';
import { funnelEventAlreadyTracked, markFunnelEventTracked } from 'Util/_HumanWritten/Analytics/funnelDedupe';

export interface SelectedTrips {
	departingTrip?: FerryTripEntity;
	returningTrip?: FerryTripEntity;
}

export interface BookingWizardWrapProps {
	queryParams?: ParsedUrlQuery;
}

export function BookingWizardWrap({ queryParams }: BookingWizardWrapProps) {
	const addOns = useAddOns();

	const { wizardData, setWizardData } = useBookingWizardDataStore();

	const [loading, setLoading] = useState(true);

	// Whether the journey elements (routes/locations) are still being fetched. The wizard's default
	// route is resolved from these, so we must wait for them before initialising the wizard data.
	const journeyElementsLoading = journeyElementsStore(x => x.isLoading);
	const hasInitialisedRef = useRef(false);

	useEffect(() => {
		// Wait until the journey elements have finished loading before resolving the wizard's default
		// route. If the wizard mounts before the routes arrive, the tickets tab would open with no
		// route selected and stay blank until a manual refresh (intermittent, timing-dependent bug).
		if (journeyElementsLoading || hasInitialisedRef.current) {
			return;
		}
		hasInitialisedRef.current = true;

		// A cache token exists only to carry wizard progress across a login or registration round
		// trip, and that return lands on the wizard with no query string. So query params win: they
		// describe what the user just asked for, and the branch below applies none of them.
		//
		// That is how "Bulk book" broke for customers and not for staff. LoginStep writes a token
		// whenever someone signs in inside the wizard, and it survives in localStorage until the next
		// wizard entry consumes it. On that entry the branch below restores the cached data, and then
		// returns early because the user is already logged in — applying neither the cached data nor
		// the URL. `mode=bulk` sat in the address bar while the wizard opened in normal mode. Staff
		// never sign in through the wizard, so they never write a token and never took this path.
		const cachedDataToken = getBookingWizardDataCacheToken();

		if (cachedDataToken != null && queryParams) {
			// Drop it rather than leave it: an unconsumed token would resurface on the next entry and
			// overwrite that one with progress from a flow the user has long since left.
			removeBookingWizardDataCacheToken();
		}

		// Only run once to avoid overwriting wizardData during the booking flow
		if (cachedDataToken != null && !queryParams) {
			getCachedBookingWizardData()
				.then(data => {
					// If user is already logged in, they just logged in (not registered)
					// Don't overwrite their current progress with old cached data
					if (wizardData.userId !== '') {
						setLoading(false);
						return;
					}

					// User registered - restore their cached progress
					setWizardData(data);
					setLoading(false);
				})
				.catch(e => {
					console.error(e);
					setWizardData(transformWizardDataFromQueryParams(queryParams));
					setLoading(false);
				});
			return;
		}

		setWizardData(transformWizardDataFromQueryParams(queryParams));
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [journeyElementsLoading]);

	// Fire `begin_checkout` once when the booking flow starts (wizard finished loading).
	// Route/items aren't necessarily chosen yet at this point, so we keep it minimal.
	//
	// The ref covers a repeat within this mount; `funnelDedupe` covers leaving the wizard and
	// coming back to the same checkout, which remounts this wrapper and resets the ref.
	const beginCheckoutTrackedRef = useRef(false);
	useEffect(() => {
		if (loading || beginCheckoutTrackedRef.current || funnelEventAlreadyTracked('begin_checkout')) {
			return;
		}
		beginCheckoutTrackedRef.current = true;
		markFunnelEventTracked('begin_checkout');
		trackBeginCheckout({ booking_type: analyticsBookingType(wizardData.tabSelected) });
	}, [loading]);

	useEffect(() => {
		// Ensure the logged in customer has the correct userId in the wizard data
		if (store.isStaff || !store.userData?.id) {
			return;
		}

		// Read the store, not this render's `wizardData`.
		//
		// Both this effect and the initialisation effect above run in the same commit whenever the
		// journey elements have already loaded — which is exactly what happens arriving from the
		// ferry schedule. The initialisation effect writes the data built from the URL, but the
		// closure here still holds the render's pre-initialisation copy, so spreading it put the
		// wizard back to where it was a moment earlier. `?mode=bulk` was applied and then silently
		// discarded, and only when the two effects landed together — which is why bulk booking
		// failed intermittently rather than every time.
		//
		// Only customers reach this at all (`store.isStaff` returns above), which is why staff never
		// saw it. Same hazard, same fix, as BulkTicketsReviewTab's `freshWizardData`.
		const currentWizardData = useBookingWizardDataStore.getState().wizardData;

		if (store.userData.id !== currentWizardData.userId) {
			setWizardData(new BookingWizardData({ ...currentWizardData, userId: store.userData.id }));
		}
	}, [store.userData, wizardData]);

	// Staff without BookOnBehalfOfCustomer may not book at all - the server refuses them every booking,
	// their own account included - so the wizard is closed to them rather than failing at reservation.
	if (!store.mayMakeBookings) {
		return <Navigate to="/403" replace />;
	}

	// Hold the wizard in its loading state until the route data is ready and the wizard data has been
	// initialised, so the tickets tab always opens with a route selected.
	if (loading) {
		return <LottieSpinner />;
	}

	return (
		<PaymentCheckOutProvider>
			<BookingWizardWrapInner />
		</PaymentCheckOutProvider>
	);
}

const transformWizardDataFromQueryParams = (queryParams?: ParsedUrlQuery) => {
	const bookingWizardPageParams = getBookingWizardParameters(queryParams);
	const bookingWizardData = getBookingWizardData(bookingWizardPageParams);

	ensureUsableJourney(bookingWizardData);
	return bookingWizardData;
};

/**
 * Make sure the wizard opens on a journey it can actually ask the server about — both ends real, and
 * the destination reachable from the origin.
 *
 * This used to test the origin alone and, when it was missing, set BOTH ends from the first route. A
 * link carrying a good origin and an empty destination therefore walked straight through with no
 * destination at all: the tickets tab showed "Somewhere to ___" and the trips request came back with
 * nothing, and the only way out was to flip the direction by hand. The schedule's Book button can
 * produce exactly that link, because it resolves each end from the trip's own stops
 * (`FerryTripCard`) and falls back to an empty string when a stop is not among the journey elements.
 *
 * A pair that is already real is left alone. Whether a route connects it is the server's call — this
 * only repairs ends that are missing or unrecognised.
 */
const ensureUsableJourney = (bookingWizardData: BookingWizardData) => {
	const { locations, routes, segments, stops } = journeyElementsStore.getState();
	if (routes.length === 0) {
		return;
	}

	const isKnownLocation = (id?: string) => !!id && locations.some(x => x.id === id);

	if (isKnownLocation(bookingWizardData.fromLocationId) && isKnownLocation(bookingWizardData.toLocationId)) {
		return;
	}

	// A real origin is worth keeping, so a half-filled link still lands the customer on the route
	// they picked rather than on whichever route happens to be first.
	if (isKnownLocation(bookingWizardData.fromLocationId)) {
		const [onwards] = getValidDestinationLocations(
			{ locations, routes, segments, stops },
			bookingWizardData.fromLocationId,
		);
		if (onwards) {
			bookingWizardData.toLocationId = onwards.id;
			return;
		}
	}

	const [firstRoute] = routes;
	bookingWizardData.fromLocationId = firstRoute.departureId;
	bookingWizardData.toLocationId = firstRoute.destinationId;
};
